import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { GroupTypeBadge } from '@/components/ui/group-type-badge'
import { 
  Trophy, Users, Shield, Settings, Sparkles,
  ArrowLeft, MoreHorizontal, LogOut
} from 'lucide-react'
import type { Group, SeasonWithBrackets, SignupSeasonWithState, Organiser } from './types'
import { getInitials } from './utils'

interface GroupHeaderProps {
  group: Group
  activeSeasons: SeasonWithBrackets[]
  upcomingSignups: SignupSeasonWithState[]
  organisers: Organiser[]
  memberCount: number
  isAuthenticated: boolean
  isMember: boolean
  canManageGroup: boolean
  isOwner: boolean
  onLeaveGroup: () => void
}

export function GroupHeader({
  group,
  activeSeasons,
  upcomingSignups,
  organisers,
  memberCount,
  isAuthenticated,
  isMember,
  canManageGroup,
  isOwner,
  onLeaveGroup,
}: GroupHeaderProps) {
  const navigate = useNavigate()

  return (
    <div className="mb-6">
      {isAuthenticated && (
        <Button variant="ghost" size="sm" onClick={() => navigate('/groups')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to My Groups
        </Button>
      )}
      
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 sm:gap-4 min-w-0">
          <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-xl bg-primary/10 flex items-center justify-center text-2xl sm:text-3xl overflow-hidden shrink-0">
            {group.icon?.startsWith('data:') || group.icon?.startsWith('https://') ? (
              <img src={group.icon} alt={group.name} className="h-full w-full object-cover" />
            ) : (
              group.icon || '🏸'
            )}
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold flex flex-wrap items-center gap-2">
              <span className="truncate">{group.name}</span>
              <GroupTypeBadge type={group.type} />
              {canManageGroup && (
                <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 flex items-center gap-1 shrink-0">
                  <Shield className="h-3 w-3" />
                  Admin
                </span>
              )}
            </h1>
            <div className="text-sm sm:text-base text-muted-foreground flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-1 cursor-default">
                    <Users className="h-4 w-4" />
                    <span>{memberCount || group.memberCount}</span>
                    <span className="hidden sm:inline">members</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{memberCount || group.memberCount} members</TooltipContent>
              </Tooltip>
              {activeSeasons.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-1 cursor-default">
                      <Trophy className="h-4 w-4" />
                      <span>{activeSeasons.length}</span>
                      <span className="hidden sm:inline">active season{activeSeasons.length !== 1 ? 's' : ''}</span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{activeSeasons.length} active season{activeSeasons.length !== 1 ? 's' : ''}</TooltipContent>
                </Tooltip>
              )}
              {upcomingSignups.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 cursor-default">
                      <Sparkles className="h-4 w-4" />
                      <span>{upcomingSignups.length}</span>
                      <span className="hidden sm:inline">open for signup</span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{upcomingSignups.length} open for signup</TooltipContent>
                </Tooltip>
              )}
            </div>
            {/* Organisers inline */}
            {(group.showOrganisers !== false) && organisers.length > 0 && (
              <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                <span>Organised by</span>
                <div className="flex items-center -space-x-1">
                  {organisers.slice(0, 5).map((organiser) => (
                    <Tooltip key={organiser.userId}>
                      <TooltipTrigger asChild>
                        <Avatar
                          className="h-7 w-7 border-2 border-background bg-muted cursor-default hover:z-10 hover:scale-110 transition-transform"
                        >
                          <AvatarImage src={organiser.avatar} alt={organiser.displayName} />
                          <AvatarFallback className="text-[10px]">
                            {getInitials(organiser.displayName)}
                          </AvatarFallback>
                        </Avatar>
                      </TooltipTrigger>
                      <TooltipContent>
                        {organiser.displayName}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                  {organisers.length > 5 && (
                    <span className="ml-2 text-xs">+{organisers.length - 5}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {isMember && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="shrink-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canManageGroup && (
                <>
                  <DropdownMenuItem onClick={() => navigate(`/group/${group.id}/admin`)}>
                    <Settings className="h-4 w-4 mr-2" />
                    Manage Group
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              {!isOwner && (
                <DropdownMenuItem 
                  onClick={onLeaveGroup}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Leave Group
                </DropdownMenuItem>
              )}
              {isOwner && (
                <DropdownMenuItem disabled className="text-muted-foreground">
                  <LogOut className="h-4 w-4 mr-2" />
                  <span className="text-xs">Owners cannot leave</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )
}
